import { useCallback, useMemo } from "react"
import { useSetState } from "@/reactUse"
import { createModel } from "hox"
import { getAllAreaPolygon } from "@/api/areas"
import { getAllAreaPolygon as getAllAreaPolygon2 } from "@/api/sharedBoard"
import useUserModel from "./useUserModel"
import useShareModel from "./useShareModel"
export type Area = {
  label: string
  value: string | number
  children?: Area[]
  resLabel?: string
}
const zoomMap = {
  110000: 7, //北京市
  120000: 7, //天津市
  130000: 5, //河北省
  140000: 5, //山西省
  150000: 5, //内蒙古自治区
  210000: 6, //辽宁省
  220000: 6, //吉林省
  230000: 5, //黑龙江省
  310000: 7, //上海市
  320000: 6, //江苏省
  330000: 6, //浙江省
  340000: 6, //安徽省
  350000: 6, //福建省
  360000: 6, //江西省
  370000: 6, //山东省
  410000: 6, //河南省
  420000: 6, //湖北省
  430000: 6, //湖南省
  440000: 6, //广东省
  450000: 6, //广西壮族自治区
  460000: 6, //海南省
  500000: 6, //重庆市
  510000: 6, //四川省
  520000: 6, //贵州省
  530000: 5, //云南省
  540000: 4, //西藏自治区
  610000: 6, //陕西省
  620000: 6, //甘肃省
  630000: 6, //青海省
  640000: 6, //宁夏回族自治区
  650000: 4, //新疆维吾尔自治区
  710000: 6, //台湾省
  810000: 7, //香港特别行政区
  820000: 7, //澳门特别行政区
}
function useAreasModel() {
  const [state, setState] = useSetState({
    tree: [],
    map: {},
    fallbackCodes: [],
  })
  const user = useUserModel()
  const shareModel = useShareModel()
  useMemo(() => {
    let api = null
    if (shareModel.isOpenShare) {
      api = getAllAreaPolygon2
    } else if (user.userInfo.potoken) {
      api = getAllAreaPolygon
    }
    api &&
      api().then(({ tree, map }) => {
        setState({ tree, map, fallbackCodes: tree.map(pro => pro.code) })
      })
  }, [user.userInfo, setState, shareModel.isOpenShare])
  /**
   * 通过value获取label
   */
  const getLabel = useCallback(
    value => {
      if (state.map[value]) {
        let item = state.map[value]
        return item["name"]
      }
      return value
    },
    [state.map]
  )

  /**
   * codes按省 市 区 分组
   */
  const getSiblingsGeo = useCallback(
    code => {
      let geo = {
        type: "FeatureCollection",
        features: [],
      }
      if (!state.tree.length) {
        return geo
      }
      let features = geo.features
      let area = state.map[code]
      if (!area) {
        console.error("area code not found: ", code)
        area = { level: 2 }
      }
      if (code % 10000 === 0) {
        // 省
        state.tree.forEach(pro => {
          features.push(pro.geo)
        })
      } else if (area.level === 2) {
        // 市
        state.tree.forEach(pro => {
          pro.sub_areas &&
            pro.sub_areas.forEach(city => {
              features.push(city.geo)
            })
        })
      } else {
        // 区
        let cityCode = Math.round(code / 100) * 100
        if (state.map[cityCode]) {
          let city = state.map[cityCode]
          city.sub_areas &&
            city.sub_areas.forEach(district => {
              features.push(district.geo)
            })
        }
      }
      return geo
    },
    [state.tree, state.map]
  )

  /**
   * 获取指定 geo
   */
  const getSubGeos = useCallback(
    code => {
      let geo = {
        type: "FeatureCollection",
        features: [],
      }
      if (!state.tree.length) {
        return geo
      }
      let features = geo.features
      let area = state.map[code]

      if (!code) {
        // 全国
        state.tree.forEach(pro => {
          features.push(pro.geo)
        })
      } else {
        // 省 or 市
        if (!area) {
          console.error("area code not found: ", code)
        } else {
          area.sub_areas &&
            area.sub_areas.forEach(a => {
              features.push(a.geo)
            })
        }
      }
      return geo
    },
    [state.tree, state.map]
  )

  const getGeoByCodes = useCallback(
    (codes, fallback = false) => {
      let geo = {
        type: "FeatureCollection",
        features: [],
      }
      if (!state.tree.length) {
        return geo
      }
      if (fallback) {
        codes = Array.from(new Set(state.fallbackCodes.concat(codes)))
      }
      let features = geo.features
      codes.forEach(code => {
        let area = state.map[code]
        if (area && area.geo) {
          features.push(area.geo)
        }
      })
      return geo
    },
    [state.tree, state.map, state.fallbackCodes]
  )
  const getByCode = useCallback(
    code => {
      let area = state.map[code]
      if (area) {
        if (!area.zoom) area.zoom = zoomMap[code] || 6
        return area
      }
      return null
    },
    [state.map]
  )

  return {
    list: state.tree,
    map: state.map,
    getLabel,
    getSiblingsGeo,
    getSubGeos,
    getGeoByCodes,
    getByCode,
  }
}

export default createModel(useAreasModel)
